<script setup lang="tsx">
import { ref, watch, nextTick, onMounted, onBeforeUnmount, onBeforeMount } from 'vue'
import http from '@/services/http'
import type {
	StandardMethodMetaData,
	MassChanges,
	MassEditRequest,
	PageRequest,
	Response,
	ApplicableMethodData,
	StandardMethod,
} from '@/models/api-generated'
import PlusButton from '@/components/widgets/PlusButton.vue'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import MassEditMethodDialog from './MassEditMethodDialog.vue'
import methodEditor from '@/stores/method-editor'
import auditStore from '@/stores/audit'
import columnStore from '@/stores/column'
import MethodMetaRow from './MethodMetaRow.vue'
import EditMethodsToolbar from './EditMethodsToolbar.vue'
import MethodFilters from './MethodFilters.vue'
import useMethodColumns from './columns'
import OrderLabel from '../widgets/OrderLabel.vue'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import JiraToolbar from '@/components/jira/JiraToolbar.vue'
import JiraExport from '../jira/JiraExport.vue'
import JiraImport from '../jira/JiraImport.vue'
import { getTypeIcon, getTypeText, hasPermission, hasRole, isRowClick, viewing } from '@/services/utils'
import { connectionColumns, visibleConnectionColumns } from './connectionColumns'
import CustomHeader from '../custom_field/CustomHeader.vue'
import CustomRow from '../custom_field/CustomRow.vue'
import { fetchCustomValues, type CustomValueMap } from '@/services/custom'
import { watchScrollFocus } from './scroll'
import AssetFilters from '../asset/AssetFilters.vue'
import DataAssetFilters from '../data_asset/DataAssetFilters.vue'
import type { MethodFilter } from '@/models/filters-generated'
import ModuleFilters from '../module/ModuleFilters.vue'
import UserFilters from '../user/UserFilters.vue'
import ConnectionColumns from './ConnectionColumns.vue'
import { PermOwnRiskRead, PermAllRiskRead, PermAllMeasureWrite, PermOwnMeasureWrite } from '@/models/enums-generated'
import { showAssociatedFilter } from './shared'
import { isEmpty } from 'lodash'
import { riskConfig, getRiskConfig } from '../risk_management/riskConfig'
import { storeToRefs } from 'pinia'

const editor = methodEditor()
const { isEditMode } = storeToRefs(editor)
const audit = auditStore()
const props = defineProps<{ businessProcess?: number }>()
const model = ref<ApplicableMethodData>()
const methodCustomValues = ref<CustomValueMap>()
const metaCustomValues = ref<CustomValueMap>()
const isLoading = ref(false)
const isAllExpanded = ref(false)

const { columns, custom, all } = useMethodColumns()
const root = columnStore().defineComponent('methods', fetch)
const assetRoot = columnStore().defineComponent('method_assets', fetch, false)
const dataAssetRoot = columnStore().defineComponent('method_data_assets', fetch, false)
const moduleRoot = columnStore().defineComponent('method_modules', fetch, false)
const userRoot = columnStore().defineComponent('method_users', fetch, false)

const scrollContainer = ref<HTMLElement | null>(null)
const showLeft = ref(false)
const showRight = ref(false)

function canEditImpl(meta: StandardMethodMetaData) {
	let result: boolean | undefined
	if (hasPermission(PermAllMeasureWrite)) result = true
	else if (hasPermission(PermOwnMeasureWrite)) {
		if (meta.responsible?.type === 'users') result = result || meta.responsible.id === sessionMap.user.id
		else if (meta.responsible?.type === 'roles') result = result || hasRole(meta.responsible.id)
		if (meta.serviceProvider?.type === 'users') result = result || meta.serviceProvider.id === sessionMap.user.id
		else if (meta.serviceProvider?.type === 'roles') result = result || hasRole(meta.serviceProvider.id)
	}
	result ??= false
	return result
}

function updateArrows() {
	if (props.businessProcess) return
	const el = scrollContainer.value
	if (!el) return
	const overshoot = 1
	const canScroll = el.scrollWidth > el.clientWidth
	showLeft.value = canScroll && el.scrollLeft > overshoot
	showRight.value = canScroll && el.scrollLeft + el.clientWidth < el.scrollWidth - overshoot
}

function scrollLeft() {
	scrollContainer.value?.scrollBy({ left: -scrollContainer.value.clientWidth, behavior: 'smooth' })
}

function scrollRight() {
	scrollContainer.value?.scrollBy({ left: scrollContainer.value.clientWidth, behavior: 'smooth' })
}

const resizeObserver = new ResizeObserver(() => updateArrows())

editor.fetchCallback = root.triggerFetch

watch(isAllExpanded, (value) => {
	nextTick(() => {
		for (let a of model.value?.methods ?? []) {
			a.isExpanded = value
		}
	})
})

watch(showAssociatedFilter, root.triggerFetch, { deep: true })

function checkAllMethods(value: boolean) {
	if (!model.value) return

	for (let i = 0; i < model.value.methods.length; i++) {
		checkAllMetas(value, i)
	}
}

function checkAllMetas(value: boolean, index: number) {
	if (!model.value) return

	model.value.methods[index].isChecked = value

	for (let j = 0; j < model.value.methods[index].metaDatas.length; j++) {
		model.value.methods[index].metaDatas[j].isChecked = value
	}

	nextTick(checkedChange)
}

function checkMeta(_value: boolean, _index: number) {
	nextTick(checkedChange)
}

async function checkedChange() {
	if (!model.value) return

	const checked: StandardMethodMetaData[] = []
	let meth: StandardMethod | null | undefined

	for (let i = 0; i < model.value.methods.length; i++) {
		for (let j = 0; j < model.value.methods[i].metaDatas.length; j++) {
			if (model.value.methods[i].metaDatas[j].isChecked) {
				checked.push(model.value.methods[i].metaDatas[j])

				if (meth === undefined) meth = model.value.methods[i]
				if (meth !== model.value.methods[i]) meth = null
			}
		}
	}

	editor.massEdit.method = meth
	editor.massEdit.selected = checked
}

async function massEdit(changes: MassChanges) {
	const request: MassEditRequest = {
		selected: editor.massEdit.selected.map((x) => x.id),
		changes,
		override: editor.massEdit.override,
	}

	editor.massEdit.selected = []
	await http.post('api/massEditMethodMetaData', request)
	await editor.refresh()

	const exportRequest = {
		smmdIDs: request.selected,
		isMethodEdit: true,
	}
	await http.post(`/api/jira/startExport`, exportRequest)
}

async function fetch(signal: AbortSignal) {
	const expandedMethods: number[] = []
	const expandedMetas: number[] = []
	for (const e of model.value?.methods ?? []) {
		if (e.isExpanded) expandedMethods.push(e.id)

		for (const f of e.metaDatas) {
			if (f.isExpanded) expandedMetas.push(f.id)
		}
	}

	let url = 'api/applicableMethods'
	if (props.businessProcess) url = `api/bp/${props.businessProcess}/applicableMethods`
	const request: PageRequest<MethodFilter> = {
		page: root.pagination.page,
		limit: root.pagination.limit,
		filter: (root.filterValue() ?? {}) as MethodFilter,
		orderBy: root.orderValue(),
	}

	if (showAssociatedFilter.value.asset) {
		const assetFilter = assetRoot.filterValue()
		if (!isEmpty(assetFilter)) request.filter.asset = assetFilter
	}
	if (showAssociatedFilter.value.dataAsset) {
		const dataAssetFilter = dataAssetRoot.filterValue()
		if (!isEmpty(dataAssetFilter)) request.filter.dataAsset = dataAssetFilter
	}
	if (showAssociatedFilter.value.module) {
		const moduleFilter = moduleRoot.filterValue()
		if (!isEmpty(moduleFilter)) request.filter.module = moduleFilter
	}
	if (showAssociatedFilter.value.user) {
		const userFilter = userRoot.filterValue()
		if (!isEmpty(userFilter)) request.filter.user = userFilter
	}

	isLoading.value = true

	const { data } = await http.post<Response<ApplicableMethodData>>(url, request, { signal })
	if (data.data?.methods.length) {
		methodCustomValues.value = await fetchCustomValues('standard_methods', ...(data.data?.methods.map((x) => x.id) || []))
		metaCustomValues.value = await fetchCustomValues(
			'standard_method_meta_data',
			...(data.data?.methods.flatMap((x) => x.metaDatas).map((x) => x.id) || []),
		)
		if (methodCustomValues.value) editor.massEdit.massMethodCustomValues = methodCustomValues.value
		if (metaCustomValues.value) editor.massEdit.massMetaCustomValues = metaCustomValues.value
	}
	if (data.data) model.value = data.data

	isLoading.value = false

	if (!model.value) return

	isAllExpanded.value = false

	for (const e of expandedMethods) {
		const method = model.value.methods.find((x) => x.id === e)
		if (method) {
			method.isExpanded = true

			for (const f of expandedMetas) {
				const meta = method.metaDatas.find((x) => x.id === f)
				if (meta) meta.isExpanded = true
			}
		}
	}

	for (let i = 0; i < model.value.methods.length; i++) {
		for (let j = 0; j < model.value.methods[i].metaDatas.length; j++) {
			editor.watchMethod(model.value.methods[i].metaDatas[j])
		}
	}

	watchCustom()
}

function watchCustom() {
	if (!model.value) return

	for (const x of custom.value) {
		for (const y of model.value.methods) {
			if (!x.value.customField) return

			if (methodCustomValues.value && x.value.customField.table === 'standard_methods') {
				editor.watchCustomMethod(x.value.customField.id, y.id, () => {
					if (methodCustomValues.value![x.value.customField!.id]) return methodCustomValues.value![x.value.customField!.id][y.id]
				})
			}

			for (const z of y.metaDatas) {
				if (metaCustomValues.value && x.value.customField.table === 'standard_method_meta_data') {
					editor.watchCustomMeta(x.value.customField.id, z.id, () => {
						if (metaCustomValues.value![x.value.customField!.id]) return metaCustomValues.value![x.value.customField!.id][z.id]
					})
				}
			}
		}
	}
	if (methodCustomValues.value) {
		editor.massEdit.massMethodCustomValues = { ...methodCustomValues.value }
	}
	if (metaCustomValues.value) {
		editor.massEdit.massMetaCustomValues = { ...metaCustomValues.value }
	}
}

function clickMethod(e: MouseEvent, i: number) {
	if (!model.value?.methods.length) return
	if (!isRowClick(e)) return

	model.value.methods[i].isExpanded = !model.value.methods[i].isExpanded
}

function statusExpand(methodIdx: number, metaDataIdx: number) {
	if (!model.value?.methods.length) return

	model.value.methods[methodIdx].isExpanded = true
	model.value.methods[methodIdx].metaDatas[metaDataIdx].isExpanded = true
}

onMounted(async () => {
	watchScrollFocus(isEditMode)
	await audit.fetch()
	await nextTick()
	const el = scrollContainer.value
	if (el) {
		el.addEventListener('scroll', updateArrows)
		resizeObserver.observe(el)
	}
	updateArrows()
})

onBeforeUnmount(() => {
	const el = scrollContainer.value
	if (el) {
		el.removeEventListener('scroll', updateArrows)
		resizeObserver.disconnect()
	}
})

onBeforeMount(async () => {
	if (viewing.value === 0) return
	if (hasPermission(PermOwnRiskRead, PermAllRiskRead)) riskConfig.value = await getRiskConfig()
})
</script>

<template>
	<JiraImport></JiraImport>
	<MethodFilters :show-instance="!props.businessProcess" :hide-button="true" class="mb-3"></MethodFilters>
	<AssetFilters v-if="showAssociatedFilter.asset" component="method_assets" class="mb-3" icon="bi-clipboard" bookmark=""></AssetFilters>
	<DataAssetFilters
		v-if="showAssociatedFilter.dataAsset"
		component="method_data_assets"
		class="mb-3"
		icon="bi-database"
		bookmark=""
	></DataAssetFilters>
	<ModuleFilters v-if="showAssociatedFilter.module" component="method_modules" class="mb-3" icon="bi-layers" bookmark=""></ModuleFilters>
	<UserFilters v-if="showAssociatedFilter.user" component="method_users" class="mb-3" icon="bi-people" bookmark=""></UserFilters>

	<div class="mb-3 row">
		<div class="col-3">
			{{ $t('measuresFound') }}: <strong>{{ model?.totalRows ?? 0 }}</strong>
		</div>
		<div class="col-6 text-center">
			{{ $t('columnsOptions') }}:
			<div class="btn-group" role="group" aria-label="Column buttons">
				<div class="btn-group" v-one-tooltip="$t('misc.filterOpt')">
					<button
						class="dropdown-toggle btn btn-outline-secondary btn-sm"
						type="button"
						id="column-picker"
						data-bs-toggle="dropdown"
						data-bs-auto-close="outside"
						aria-expanded="false"
					>
						{{ $t('columns') }}
					</button>

					<div class="dropdown-menu" id="column-picker-menu" aria-labelledby="column-picker">
						<div class="px-3 column-picker-content">
							<template v-for="(opt, i) in all" :key="opt.value.name">
								<div class="form-check">
									<input
										class="form-check-input"
										type="checkbox"
										:id="'chk-' + i"
										:value="opt.value.name"
										v-model="all[i].value.isVisible"
									/>
									<label class="form-check-label" :for="'chk-' + i">{{ opt.value.label }}</label>
								</div>
							</template>
						</div>
					</div>
				</div>
				<div class="btn-group" v-one-tooltip="$t('misc.connectionOpt')">
					<button
						class="dropdown-toggle btn btn-outline-secondary btn-sm"
						type="button"
						id="connection-column-picker"
						data-bs-toggle="dropdown"
						data-bs-auto-close="outside"
						aria-expanded="false"
					>
						{{ $t('connectionColumns') }}
					</button>

					<div class="dropdown-menu" aria-labelledby="connection-column-picker">
						<div class="px-3 column-picker-content">
							<template v-for="([name, opt], i) in Object.entries(connectionColumns)" :key="opt.text">
								<div class="form-check">
									<input class="form-check-input" type="checkbox" :id="'connection-chk-' + i" :value="name" v-model="opt.isVisible" />
									<label class="form-check-label" :for="'connection-chk-' + i">{{ $t(opt.text) }}</label>
								</div>
							</template>
						</div>
					</div>
				</div>
				<div class="btn-group" v-one-tooltip="$t('misc.connectionObjOpt')">
					<button
						class="dropdown-toggle btn btn-outline-secondary btn-sm"
						type="button"
						id="associated-filter-picker"
						data-bs-toggle="dropdown"
						data-bs-auto-close="outside"
						aria-expanded="false"
					>
						{{ $t('connections') }}
					</button>

					<div class="dropdown-menu" aria-labelledby="associated-filter-picker">
						<div class="px-3 column-picker-content">
							<div class="form-check">
								<input class="form-check-input" type="checkbox" id="associated-chk-assets" v-model="showAssociatedFilter.asset" />
								<label class="form-check-label" for="associated-chk-assets">{{ $t('assets') }}</label>
							</div>

							<div class="form-check">
								<input class="form-check-input" type="checkbox" id="associated-chk-data-assets" v-model="showAssociatedFilter.dataAsset" />
								<label class="form-check-label" for="associated-chk-data-assets">{{ $t('dataAssets') }}</label>
							</div>

							<div class="form-check">
								<input class="form-check-input" type="checkbox" id="associated-chk-modules" v-model="showAssociatedFilter.module" />
								<label class="form-check-label" for="associated-chk-modules">{{ $t('module.label', 2) }}</label>
							</div>

							<div class="form-check">
								<input class="form-check-input" type="checkbox" id="associated-chk-users" v-model="showAssociatedFilter.user" />
								<label class="form-check-label" for="associated-chk-users">{{ $t('user.label', 2) }}</label>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="col-3 text-end">
			{{ $t('measureConnectionsFound') }}: <strong>{{ model?.totalConnectionRows ?? 0 }}</strong>
		</div>
	</div>

	<LoadingContainer v-model="isLoading" class="rounded-container p-3 scroll-wrapper">
		<div class="table-responsive w-100" ref="scrollContainer">
			<table class="table">
				<div v-show="showLeft" class="scroll-arrow" @click="scrollLeft"><i class="bi bi-arrow-left-short"></i></div>

				<thead>
					<tr>
						<th class="w-auto-32" v-if="!isEditMode && !props.businessProcess">
							<input
								class="form-check-input"
								type="checkbox"
								data-test="check-all-methods"
								@change="checkAllMethods(($event.target as any).checked)"
							/>
						</th>
						<th class="w-auto-32" data-test="expand-all-methods"><PlusButton v-model="isAllExpanded"></PlusButton></th>
						<th v-if="columns.version.value.isVisible">{{ $t('version') }}</th>
						<th v-if="columns.module.value.isVisible" class="w-auto-96">{{ $t('module.label') }}</th>
						<th v-if="columns.type.value.isVisible" v-one-tooltip="$t('securityNeed.label')">
							<div class="status-column">
								<OrderLabel v-model="columns.type.value">
									<i class="bi bi-shield-exclamation"></i>
								</OrderLabel>
							</div>
						</th>
						<th v-if="columns.reference.value.isVisible" class="w-auto-144">
							<OrderLabel v-model="columns.reference.value">
								{{ $t('reference.label') }}
							</OrderLabel>
						</th>
						<th v-if="columns.desc.value.isVisible">
							<OrderLabel v-model="columns.desc.value">
								{{ $t('title') }}
							</OrderLabel>
						</th>
						<th v-if="visibleConnectionColumns.length" class="text-center">
							<div class="connection-column" v-one-tooltip="$t('connections')">
								<i class="bi bi-link-45deg"></i>
							</div>
						</th>
						<th v-for="[name, col] in visibleConnectionColumns" :key="name" class="text-center">
							<div class="connection-column" v-one-tooltip="$t(col.text)">
								<i :class="col.icon"></i>
							</div>
						</th>
						<th v-if="visibleConnectionColumns.length" class="text-center w-auto-128">{{ $t('association', 2) }}</th>
						<CustomHeader :columns="custom" table="standard_methods"></CustomHeader>
					</tr>
				</thead>
				<tbody>
					<template v-if="model">
						<template v-for="(method, i) in model.methods" :key="method.id">
							<tr class="main-row" :class="{ odd: i % 2 != 0 }" @click="clickMethod($event, i)">
								<td v-if="!isEditMode && !props.businessProcess">
									<input
										class="form-check-input"
										type="checkbox"
										v-model="model.methods[i].isChecked"
										@change="checkAllMetas(($event.target as any).checked, i)"
									/>
								</td>

								<td :data-test="'expand-method' + model.methods[i].id"><PlusButton v-model="model.methods[i].isExpanded"></PlusButton></td>
								<template v-if="columns.version.value.isVisible">
									<td v-if="method.module.version">{{ $te(method.module.version) ? $t(method.module.version) : method.module.version }}</td>
									<td v-else>-</td>
								</template>
								<td v-if="columns.module.value.isVisible">{{ method.module.name }}</td>
								<td v-if="columns.type.value.isVisible">
									<div class="icon-container" v-one-tooltip="getTypeText(method.type)">
										{{ getTypeText(method.type).charAt(0) }}
										<i :class="getTypeIcon(method.type)"></i>
									</div>
								</td>
								<td v-if="columns.reference.value.isVisible">{{ method.name }}</td>
								<td v-if="columns.desc.value.isVisible">{{ method.description }}</td>
								<ConnectionColumns
									v-model="model.methods[i]"
									:canEdit="method.metaDatas.map(canEditImpl)"
									@expand="(e) => statusExpand(i, e)"
								></ConnectionColumns>
								<CustomRow
									:columns="custom"
									v-model="methodCustomValues"
									:row-id="method.id"
									:is-edit="isEditMode"
									table="standard_methods"
								></CustomRow>
							</tr>
							<tr class="group-row" v-if="method.isExpanded">
								<td colspan="100%" class="ps-4">
									<table class="table">
										<thead>
											<tr>
												<th v-if="!isEditMode" class="w-auto-24"></th>
												<th v-if="method.metaDatas.length > 1" class="w-auto-8">
													<PlusButton
														@open="model.methods[i].metaDatas.forEach((x) => (x.isExpanded = true))"
														@close="model.methods[i].metaDatas.forEach((x) => (x.isExpanded = false))"
													></PlusButton>
												</th>
												<th class="w-auto-170">{{ $t('association') }}</th>
												<th v-if="columns.instance.value.isVisible" class="w-auto-180">{{ $t('institution') }}</th>

												<th v-if="columns.responsible.value.isVisible" class="w-auto-300">
													<OrderLabel v-model="columns.responsible.value">
														{{ $t('responsible') }}
													</OrderLabel>
												</th>

												<th v-if="columns.serviceProvider.value.isVisible" class="w-auto-300">
													<OrderLabel v-model="columns.serviceProvider.value">
														{{ $t('method.serviceProvider') }}
													</OrderLabel>
												</th>
												<th v-if="columns.priority.value.isVisible" class="w-auto-120">
													<OrderLabel v-model="columns.priority.value">
														{{ $t('priority') }}
													</OrderLabel>
												</th>
												<th v-if="columns.status.value.isVisible" class="w-auto-200">
													<OrderLabel v-model="columns.status.value">
														{{ $t('status.label') }}
													</OrderLabel>
												</th>
												<th v-if="columns.auditCheck.value.isVisible" class="w-auto-32 text-center" v-one-tooltip="$t('audit.checks')">
													<i class="bi bi-clipboard-check"></i>
												</th>
												<th v-if="columns.price.value.isVisible" class="w-auto-110">{{ $t('price') }}</th>
												<th v-if="columns.dueDate.value.isVisible" class="w-auto-140">
													<OrderLabel v-model="columns.dueDate.value">{{ $t('date.dueDate', 2) }}</OrderLabel>
												</th>
												<th v-if="columns.updatedAt.value.isVisible" class="w-auto-32 text-center" v-one-tooltip="$t('lastReview')">
													<OrderLabel v-model="columns.updatedAt.value"><i class="bi bi-calendar-check"></i></OrderLabel>
												</th>
												<th class="w-auto-32 text-center" v-one-tooltip="$t('history.label')">
													<i class="bi bi-clock-history"></i>
												</th>

												<th v-if="columns.jira.value.isVisible" class="w-auto-32">
													{{ $t('jira.label') }}
												</th>
												<CustomHeader :columns="custom" table="standard_method_meta_data"></CustomHeader>
											</tr>
										</thead>
										<tbody>
											<MethodMetaRow
												v-for="(mmd, j) in method.metaDatas"
												:key="mmd.id"
												class="blue"
												v-model="method.metaDatas[j]"
												v-model:custom-values="metaCustomValues"
												:finished-audit="audit.oldSelected"
												:method="method"
												:starts-expanded="method.metaDatas.length === 1"
												@checked-mass-edit-change="checkMeta($event, i)"
											></MethodMetaRow>
										</tbody>
									</table>
								</td>
							</tr>
						</template>
					</template>
				</tbody>
				<div v-show="showRight" class="scroll-arrow right" @click="scrollRight"><i class="bi bi-arrow-right-short"></i></div>
			</table>
		</div>
	</LoadingContainer>
	<Pagination
		v-show="!isEditMode"
		v-model="root.pagination"
		:total-pages="model?.totalPages"
		:tableTitle="props.businessProcess ? 'bp_methods' : 'methods'"
	/>

	<div id="methods-pagination" :data-page="root.pagination.page"></div>

	<MassEditMethodDialog v-model="editor.massEdit" @mass-edit="massEdit($event)"></MassEditMethodDialog>
	<EditMethodsToolbar></EditMethodsToolbar>
	<JiraToolbar></JiraToolbar>
	<JiraExport></JiraExport>

	<div v-if="isEditMode" class="pb-5"></div>
</template>

<style lang="sass" scoped>
:deep(.blue td)
  background-color: var(--bs-primary-bg-subtle)
  color: var(--bs-body-color)
  border-bottom-color: rgba(var(--bs-primary-rgb), .6)

:deep(.connection-column)
  display: flex
  flex-direction: column
  justify-content: center
</style>
